import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Clock, Trophy, Flame, Crown, Swords, Heart, Laugh, Sparkles, Ghost, Rocket, Coffee, Zap, Film, ChevronRight, Star, Award, BookOpen, Play } from 'lucide-react';
import { getTrendingComics, getNewComics, getCurrentWeekPrizes, getComics, seedDatabase, getComic } from '../services/api';
import { ComicCard, ComicCardSkeleton } from '../components/ComicCard';
import { ComicCarousel } from '../components/ComicCarousel';
import { AdBanner, InlineAd } from '../components/AdBanner';
import { useReadingProgress } from '../hooks/useReadingProgress';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

// Genre configuration with icons and colors
const GENRE_CONFIG = {
  action: { icon: Swords, color: 'text-red-500', label: 'Action' },
  romance: { icon: Heart, color: 'text-pink-500', label: 'Romance' },
  comedy: { icon: Laugh, color: 'text-yellow-500', label: 'Comedy' },
  fantasy: { icon: Sparkles, color: 'text-purple-500', label: 'Fantasy' },
  horror: { icon: Ghost, color: 'text-gray-400', label: 'Horror' },
  'sci-fi': { icon: Rocket, color: 'text-cyan-500', label: 'Sci-Fi' },
  slice_of_life: { icon: Coffee, color: 'text-amber-500', label: 'Slice of Life' },
  superhero: { icon: Zap, color: 'text-blue-500', label: 'Superhero' },
  thriller: { icon: Film, color: 'text-emerald-500', label: 'Thriller' },
};

// Hero banner slides with anime images
const HERO_SLIDES = [
  {
    id: 1,
    image: 'https://static.prod-images.emergentagent.com/jobs/1ce97608-8264-49f3-97d7-634e221d254b/images/efdec8041a831d2cc56dd2c191616a0f41b38306a8b07efc3faa822ccf49643b.png',
    title: 'Epic Fantasy Adventures',
    subtitle: 'Discover legendary heroes and magical worlds',
    genre: 'Fantasy',
    color: '#3B82F6'
  },
  {
    id: 2,
    image: 'https://static.prod-images.emergentagent.com/jobs/1ce97608-8264-49f3-97d7-634e221d254b/images/c8a466a139082f2eedb7215bbc5eb979cda53cae94be9ecdf3a51f8a5b08d940.png',
    title: 'Action & Martial Arts',
    subtitle: 'Heart-pounding battles and intense combat',
    genre: 'Action',
    color: '#EF4444'
  },
  {
    id: 3,
    image: 'https://static.prod-images.emergentagent.com/jobs/1ce97608-8264-49f3-97d7-634e221d254b/images/057f0f6594bbab476f1617efab711fa74f37a52dc155e446eefd15661e2241d1.png',
    title: 'Magic & Mystery',
    subtitle: 'Uncover secrets in enchanted realms',
    genre: 'Fantasy',
    color: '#8B5CF6'
  },
  {
    id: 4,
    image: 'https://static.prod-images.emergentagent.com/jobs/1ce97608-8264-49f3-97d7-634e221d254b/images/6d60719feee0d7ec1720fcd6360a330d5d52160406eff267097f1156203e7923.png',
    title: 'Romance Stories',
    subtitle: 'Love stories that touch your heart',
    genre: 'Romance',
    color: '#EC4899'
  }
];

function HomePage() {
  const [trendingComics, setTrendingComics] = useState([]);
  const [newComics, setNewComics] = useState([]);
  const [genreComics, setGenreComics] = useState({});
  const [prizes, setPrizes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genresLoading, setGenresLoading] = useState(true);
  const [continueReading, setContinueReading] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { getAllProgress } = useReadingProgress();

  // Auto-transition hero slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % HERO_SLIDES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        await seedDatabase();
        
        const [trendingRes, newRes, prizesRes] = await Promise.all([
          getTrendingComics(12),
          getNewComics(12),
          getCurrentWeekPrizes()
        ]);
        
        setTrendingComics(trendingRes.data);
        setNewComics(newRes.data);
        setPrizes(prizesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load content');
      } finally {
        setLoading(false);
      }
    }
    
    fetchData();
  }, []);

  // Fetch comics by genre
  useEffect(() => {
    async function fetchGenreComics() {
      try {
        const genres = ['action', 'romance', 'fantasy', 'horror', 'sci-fi'];
        const promises = genres.map(genre => 
          getComics({ genre, limit: 12 }).then(res => ({ genre, comics: res.data }))
        );
        
        const results = await Promise.all(promises);
        const genreData = {};
        results.forEach(({ genre, comics }) => {
          if (comics.length > 0) {
            genreData[genre] = comics;
          }
        });
        setGenreComics(genreData);
      } catch (error) {
        console.error('Error fetching genre comics:', error);
      } finally {
        setGenresLoading(false);
      }
    }
    
    fetchGenreComics();
  }, []);

  // Fetch continue reading comics
  useEffect(() => {
    async function fetchContinueReading() {
      try {
        const progressList = getAllProgress();
        if (progressList.length === 0) return;

        const comicPromises = progressList.slice(0, 4).map(async (p) => {
          try {
            const res = await getComic(p.comicId);
            return {
              ...res.data,
              progress: p
            };
          } catch {
            return null;
          }
        });

        const comics = await Promise.all(comicPromises);
        setContinueReading(comics.filter(Boolean));
      } catch (error) {
        console.error('Error fetching continue reading:', error);
      }
    }

    fetchContinueReading();
  }, [getAllProgress]);

  return (
    <div className="min-h-screen pb-24" data-testid="home-page">
      {/* SEO */}
      <SEO 
        title={null}
        description="Discover, read, and create amazing web comics on Supremics. Join our community of passionate creators and readers."
      />

      {/* Hero Banner Carousel - Toomics Style */}
      <section className="relative h-[50vh] min-h-[400px] max-h-[500px] overflow-hidden bg-black" data-testid="hero-section">
        {/* Sliding Banners */}
        {HERO_SLIDES.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              index === currentSlide 
                ? 'opacity-100 translate-x-0' 
                : index < currentSlide 
                  ? 'opacity-0 -translate-x-full' 
                  : 'opacity-0 translate-x-full'
            }`}
          >
            {/* Background Image */}
            <img 
              src={slide.image} 
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30" />
            
            {/* Content */}
            <div className="absolute inset-0 flex items-center">
              <div className="page-padding w-full">
                <div className="max-w-xl">
                  <span 
                    className="inline-block px-3 py-1 text-xs font-bold text-white rounded mb-4"
                    style={{ backgroundColor: slide.color }}
                  >
                    {slide.genre}
                  </span>
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-teko font-bold text-white leading-tight mb-3">
                    {slide.title}
                  </h2>
                  <p className="text-white/70 text-lg mb-6">
                    {slide.subtitle}
                  </p>
                  <div className="flex gap-3">
                    <Link 
                      to="/browse" 
                      className="btn-hotrod px-8 py-3 text-base"
                    >
                      <span>Start Reading</span>
                    </Link>
                    <Link 
                      to="/pricing" 
                      className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
                    >
                      <Zap size={18} />
                      Free Trial
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Navigation Arrows */}
        <button 
          onClick={() => setCurrentSlide(prev => prev === 0 ? HERO_SLIDES.length - 1 : prev - 1)}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors z-20"
        >
          <ChevronRight size={24} className="rotate-180" />
        </button>
        <button 
          onClick={() => setCurrentSlide(prev => (prev + 1) % HERO_SLIDES.length)}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors z-20"
        >
          <ChevronRight size={24} />
        </button>
        
        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {HERO_SLIDES.map((slide, index) => (
            <button
              key={`slide-${slide.id || index}`}
              onClick={() => setCurrentSlide(index)}
              className={`h-1 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'w-8 bg-white' 
                  : 'w-4 bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
        
        {/* Slide Counter */}
        <div className="absolute bottom-6 right-6 text-white/60 text-sm font-mono z-20">
          <span className="text-white font-bold">{currentSlide + 1}</span> / {HERO_SLIDES.length}
        </div>
      </section>

      {/* Quick Action Bar */}
      <section className="bg-[#111] border-b border-[#222]">
        <div className="page-padding py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-6 overflow-x-auto">
              <Link to="/browse?genre=action" className="flex items-center gap-2 text-white/70 hover:text-[#E63946] transition-colors whitespace-nowrap">
                <Swords size={18} /> Action
              </Link>
              <Link to="/browse?genre=romance" className="flex items-center gap-2 text-white/70 hover:text-[#E63946] transition-colors whitespace-nowrap">
                <Heart size={18} /> Romance
              </Link>
              <Link to="/browse?genre=fantasy" className="flex items-center gap-2 text-white/70 hover:text-[#E63946] transition-colors whitespace-nowrap">
                <Sparkles size={18} /> Fantasy
              </Link>
              <Link to="/browse?genre=horror" className="flex items-center gap-2 text-white/70 hover:text-[#E63946] transition-colors whitespace-nowrap">
                <Ghost size={18} /> Horror
              </Link>
            </div>
            {prizes && (
              <Link to="/leaderboard" className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400 transition-colors whitespace-nowrap">
                <Trophy size={18} />
                <span className="font-bold">${prizes.prize_amount}</span>
                <span className="text-white/50 text-sm hidden sm:inline">Weekly Prize</span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Continue Reading Section */}
      {continueReading.length > 0 && (
        <section className="page-padding mt-16" data-testid="continue-reading-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs tracking-[0.3em] text-[var(--primary)] uppercase mb-1">Pick up where you left off</p>
              <h2 className="text-3xl font-teko font-semibold">Continue Reading</h2>
            </div>
            <Link 
              to="/dashboard" 
              className="text-sm text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors flex items-center gap-1"
            >
              View History <ChevronRight size={16} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {continueReading.map(comic => (
              <Link
                key={comic.id}
                to={`/read/${comic.id}/${comic.progress?.episodeId || ''}`}
                className="group flex gap-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4 hover:border-[var(--primary)]/50 transition-all"
                data-testid={`continue-${comic.id}`}
              >
                <div className="relative w-16 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                  <img 
                    src={comic.cover_image} 
                    alt={comic.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play size={20} className="text-white" fill="white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-teko text-lg line-clamp-1 group-hover:text-[var(--primary)] transition-colors">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-[var(--text-secondary)] flex items-center gap-1 mt-1">
                    <BookOpen size={12} />
                    Episode {comic.progress?.episodeNumber || 1}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)] flex items-center gap-1 mt-1">
                    <Clock size={12} />
                    {new Date(comic.progress?.lastRead).toLocaleDateString()}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Trending Carousel - Otaku Style */}
      <section className="mt-20" data-testid="trending-section">
        <ComicCarousel
          subtitle="Top new releases"
          title="Popular Comics This Week"
          comics={trendingComics}
          loading={loading}
          viewAllLink="/browse?sort=views"
        />
      </section>

      {/* Ad after Trending */}
      <InlineAd className="mt-8" />

      {/* New Releases Carousel */}
      <section className="mt-16" data-testid="new-releases-section">
        <ComicCarousel
          subtitle="Fresh content"
          title="New Releases"
          comics={newComics}
          loading={loading}
          viewAllLink="/browse?sort=created_at"
        />
      </section>

      {/* Ad Placement - Full Width */}
      <section className="page-padding mt-20">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden">
          <AdBanner 
            size="leaderboard" 
            placement="homepage"
            variant="prominent"
            className="w-full min-h-[120px]"
          />
        </div>
      </section>

      {/* Genre Carousels - Otaku Style */}
      {Object.entries(genreComics).map(([genre, comics], index) => {
        const config = GENRE_CONFIG[genre];
        if (!config || comics.length === 0) return null;
        
        return (
          <React.Fragment key={genre}>
            <section className="mt-16" data-testid={`genre-section-${genre}`}>
              <ComicCarousel
                subtitle={`${config.label} comics`}
                title={config.label}
                comics={comics}
                loading={genresLoading}
                viewAllLink={`/browse?genre=${genre}`}
              />
            </section>
            {/* Ad after every 2 genre carousels */}
            {(index + 1) % 2 === 0 && index < Object.entries(genreComics).length - 1 && (
              <InlineAd className="mt-4" />
            )}
          </React.Fragment>
        );
      })}

      {/* Ad before CTA */}
      <InlineAd className="mt-8" />

      {/* CTA Section */}
      <section className="section-spacing page-padding">
        <div className="relative bg-[var(--surface)] border border-[var(--border)] p-8 md:p-12 overflow-hidden rounded-lg">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[var(--primary)]/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[var(--secondary)]/10 blur-3xl" />
          
          <div className="relative z-10 max-w-2xl">
            <p className="text-[11px] tracking-[0.3em] text-[var(--primary)] uppercase mb-3">Join us</p>
            <h2 className="text-4xl md:text-5xl font-teko mb-4">
              Ready to Create?
            </h2>
            <p className="text-[var(--text-secondary)] mb-6">
              Join as a Creator for just $4/month. Publish unlimited comics, receive donations from fans, and compete for weekly prizes.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/pricing" className="btn-hotrod" data-testid="cta-pricing-btn">
                <span>See Plans</span>
              </Link>
              <Link to="/signup" className="btn-outline" data-testid="cta-signup-btn">
                <span>Sign Up Free</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
